import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { toast } from 'sonner';
import api from '../api';
import {
  TrendingUp, TrendingDown, Users, DollarSign, Target, AlertTriangle,
  Award, Plus, Edit2, Trash2, ArrowUpRight, ArrowDownRight,
  BarChart3, Briefcase, UserCheck, UserX, ChevronRight, RefreshCw,
  ChevronDown, ChevronUp, Calendar, Download, Check, ChevronsUpDown, Search, Upload, ArrowUpDown
} from 'lucide-react';

const formatCTC = (v) => {
  if (!v) return '₹0';
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};

const formatRevenue = (v) => {
  if (!v || v === 0) return '0';
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};

const StatusBadge = ({ status }) => {
  const map = {
    selected: { label: 'Selected', cls: 'bg-blue-100 text-blue-700' },
    offered: { label: 'Offered', cls: 'bg-amber-100 text-amber-700' },
    joined: { label: 'Joined', cls: 'bg-emerald-100 text-emerald-700' },
    dropped: { label: 'Dropped', cls: 'bg-red-100 text-red-700' },
  };
  const s = map[status] || { label: status, cls: 'bg-slate-100 text-slate-700' };
  return <Badge className={s.cls} data-testid={`status-badge-${status}`}>{s.label}</Badge>;
};

// ========= KPI Card =========
const KPICard = ({ title, value, subtitle, icon: Icon, trend, color = 'indigo' }) => (
  <Card data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</p>
          <p className={`text-2xl font-bold mt-1 text-${color}-600`}>{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-${color}-50`}>
          <Icon size={20} className={`text-${color}-500`} />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          {trend >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          <span>{Math.abs(trend)}%</span>
        </div>
      )}
    </CardContent>
  </Card>
);

// ========= CLIENT BILLING DIALOG =========
const INDUSTRY_OPTIONS = [
  'IT Services', 'Insurance', 'BFSI', 'Manufacturing', 'Healthcare', 'Pharma',
  'Automobile', 'Retail', 'E-Commerce', 'Telecom', 'FMCG', 'Real Estate',
  'Logistics', 'Education', 'BPO / KPO', 'Consulting', 'Energy', 'Media',
  'Hospitality', 'Agriculture', 'Legal', 'Aviation', 'Government / PSU',
];

const ClientBillingDialog = ({ open, onClose, onSave, editing }) => {
  const [form, setForm] = useState({ client_name: '', billing_type: 'flat', flat_fee: '', percentage: '', industry: '', notes: '' });
  const [industryOpen, setIndustryOpen] = useState(false);
  const [customIndustry, setCustomIndustry] = useState('');

  useEffect(() => {
    if (editing) setForm({
      client_name: editing.client_name || '',
      billing_type: editing.billing_type || 'flat',
      flat_fee: editing.flat_fee ?? '',
      percentage: editing.percentage ?? '',
      industry: editing.industry || '',
      notes: editing.notes || '',
    });
    else setForm({ client_name: '', billing_type: 'flat', flat_fee: '', percentage: '', industry: '', notes: '' });
    setCustomIndustry('');
  }, [editing, open]);

  const handleSubmit = () => {
    if (!form.client_name) return toast.error('Client name is required');
    if (form.billing_type === 'flat' && !form.flat_fee) return toast.error('Flat fee is required');
    if (form.billing_type === 'percentage' && !form.percentage) return toast.error('Percentage is required');
    onSave({
      ...form,
      flat_fee: form.flat_fee ? parseFloat(form.flat_fee) : null,
      percentage: form.percentage ? parseFloat(form.percentage) : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="client-billing-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Client Billing' : 'Add Client'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Client Name</Label>
            <Input data-testid="client-name-input" value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} />
          </div>
          <div>
            <Label>Billing Type</Label>
            <Select value={form.billing_type} onValueChange={v => setForm(f => ({ ...f, billing_type: v }))}>
              <SelectTrigger data-testid="billing-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="flat">Flat Fee per Joining</SelectItem>
                <SelectItem value="percentage">% of Candidate CTC</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.billing_type === 'flat' && (
            <div>
              <Label>Flat Fee (INR)</Label>
              <Input data-testid="flat-fee-input" type="number" value={form.flat_fee} onChange={e => setForm(f => ({ ...f, flat_fee: e.target.value }))} />
            </div>
          )}
          {form.billing_type === 'percentage' && (
            <div>
              <Label>Percentage (%)</Label>
              <Input data-testid="percentage-input" type="number" step="0.1" value={form.percentage} onChange={e => setForm(f => ({ ...f, percentage: e.target.value }))} />
            </div>
          )}
          <div>
            <Label>Industry</Label>
            <Popover open={industryOpen} onOpenChange={setIndustryOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-between font-normal" data-testid="client-industry-select">
                  {form.industry || 'Select industry...'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search or type new..." value={customIndustry} onValueChange={setCustomIndustry} data-testid="industry-search-input" />
                  <CommandList className="max-h-[200px] overflow-y-auto">
                    <CommandEmpty className="p-0" />
                    <CommandGroup>
                      {INDUSTRY_OPTIONS
                        .filter(ind => !customIndustry || ind.toLowerCase().includes(customIndustry.toLowerCase()))
                        .map(ind => (
                          <CommandItem key={ind} value={ind} onSelect={() => { setForm(f => ({ ...f, industry: ind })); setIndustryOpen(false); setCustomIndustry(''); }} data-testid={`industry-option-${ind}`}>
                            <Check className={`mr-2 h-4 w-4 ${form.industry === ind ? 'opacity-100' : 'opacity-0'}`} />
                            {ind}
                          </CommandItem>
                        ))}
                      {customIndustry && !INDUSTRY_OPTIONS.some(i => i.toLowerCase() === customIndustry.toLowerCase()) && (
                        <CommandItem value={customIndustry} onSelect={() => { setForm(f => ({ ...f, industry: customIndustry })); setIndustryOpen(false); setCustomIndustry(''); }} data-testid="industry-custom-option">
                          <Plus className="mr-2 h-4 w-4 text-indigo-500" />
                          <span>Add "<strong>{customIndustry}</strong>"</span>
                        </CommandItem>
                      )}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label>Notes</Label>
            <Input data-testid="client-notes-input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} data-testid="save-client-btn">{editing ? 'Update' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ========= SEARCHABLE EMPLOYEE PICKER =========
const SearchableEmployeePicker = ({ value, onSelect, employees, label, testId }) => {
  const [open, setOpen] = useState(false);
  const selected = employees.find(e => e.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal" data-testid={testId}>
          {selected ? `${selected.name} (${selected.employee_id})` : `Select ${label}...`}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${label}...`} data-testid={`${testId}-search`} />
          <CommandList className="max-h-[200px]">
            <CommandEmpty>No {label} found.</CommandEmpty>
            <CommandGroup>
              {employees.map(e => (
                <CommandItem key={e.id} value={`${e.name} ${e.employee_id}`} onSelect={() => { onSelect(e.id); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === e.id ? 'opacity-100' : 'opacity-0'}`} />
                  {e.name} ({e.employee_id})
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// ========= PLACEMENT DIALOG =========
const PlacementDialog = ({ open, onClose, onSave, editing, recruiters, clients }) => {
  const [form, setForm] = useState({
    recruiter_id: '', client_billing_id: '', candidate_name: '', candidate_email: '',
    candidate_phone: '', position: '', candidate_ctc: '', status: 'selected',
    selection_date: '', joining_date: '', notes: '',
  });

  useEffect(() => {
    if (editing) setForm({
      recruiter_id: editing.recruiter_id || '',
      client_billing_id: editing.client_billing_id || '',
      candidate_name: editing.candidate_name || '',
      candidate_email: editing.candidate_email || '',
      candidate_phone: editing.candidate_phone || '',
      position: editing.position || '',
      candidate_ctc: editing.candidate_ctc ?? '',
      status: editing.status || 'selected',
      selection_date: editing.selection_date || '',
      joining_date: editing.joining_date || '',
      notes: editing.notes || '',
    });
    else setForm({ recruiter_id: '', client_billing_id: '', candidate_name: '', candidate_email: '', candidate_phone: '', position: '', candidate_ctc: '', status: 'selected', selection_date: new Date().toISOString().slice(0, 10), joining_date: '', notes: '' });
  }, [editing, open]);

  const handleSubmit = () => {
    if (!form.recruiter_id) return toast.error('Select a recruiter');
    if (!form.client_billing_id) return toast.error('Select a client');
    if (!form.candidate_name) return toast.error('Candidate name is required');
    if (!form.position) return toast.error('Position is required');
    onSave({
      ...form,
      candidate_ctc: form.candidate_ctc ? parseFloat(form.candidate_ctc) : 0,
      selection_date: form.selection_date || null,
      joining_date: form.joining_date || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="placement-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Update Placement' : 'Add Placement'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          <div>
            <Label>Recruiter</Label>
            <SearchableEmployeePicker value={form.recruiter_id} onSelect={v => setForm(f => ({ ...f, recruiter_id: v }))} employees={recruiters} label="recruiter" testId="recruiter-select" />
          </div>
          <div>
            <Label>Client</Label>
            <Select value={form.client_billing_id} onValueChange={v => setForm(f => ({ ...f, client_billing_id: v }))}>
              <SelectTrigger data-testid="client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_name} ({c.billing_type === 'flat' ? `Flat: ${c.flat_fee}` : `${c.percentage}% CTC`})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Candidate Name</Label>
              <Input data-testid="candidate-name-input" value={form.candidate_name} onChange={e => setForm(f => ({ ...f, candidate_name: e.target.value }))} />
            </div>
            <div>
              <Label>Position</Label>
              <Input data-testid="position-input" value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input data-testid="candidate-email-input" value={form.candidate_email} onChange={e => setForm(f => ({ ...f, candidate_email: e.target.value }))} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="candidate-phone-input" value={form.candidate_phone} onChange={e => setForm(f => ({ ...f, candidate_phone: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Candidate CTC (Annual INR)</Label>
              <Input data-testid="candidate-ctc-input" type="number" value={form.candidate_ctc} onChange={e => setForm(f => ({ ...f, candidate_ctc: e.target.value }))} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="selected">Selected</SelectItem>
                  <SelectItem value="offered">Offered</SelectItem>
                  <SelectItem value="joined">Joined</SelectItem>
                  <SelectItem value="dropped">Dropped</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Selection Date</Label>
              <Input data-testid="selection-date-input" type="date" value={form.selection_date} onChange={e => setForm(f => ({ ...f, selection_date: e.target.value }))} />
            </div>
            <div>
              <Label>Joining Date</Label>
              <Input data-testid="joining-date-input" type="date" value={form.joining_date} onChange={e => setForm(f => ({ ...f, joining_date: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Input data-testid="placement-notes-input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} data-testid="save-placement-btn">{editing ? 'Update' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ========= STATUS UPDATE DIALOG (quick) =========
const StatusUpdateDialog = ({ open, onClose, onSave, placement }) => {
  const [status, setStatus] = useState('');
  const [joiningDate, setJoiningDate] = useState('');
  const [dropReason, setDropReason] = useState('');

  useEffect(() => {
    if (placement) {
      setStatus(placement.status);
      setJoiningDate(placement.joining_date || '');
      setDropReason(placement.drop_reason || '');
    }
  }, [placement, open]);

  const handleSave = () => {
    const update = { status };
    if (status === 'joined' && joiningDate) update.joining_date = joiningDate;
    if (status === 'dropped' && dropReason) update.drop_reason = dropReason;
    onSave(update);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="status-update-dialog">
        <DialogHeader>
          <DialogTitle>Update Status: {placement?.candidate_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="update-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="selected">Selected</SelectItem>
                <SelectItem value="offered">Offered</SelectItem>
                <SelectItem value="joined">Joined</SelectItem>
                <SelectItem value="dropped">Dropped</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {status === 'joined' && (
            <div>
              <Label>Joining Date</Label>
              <Input data-testid="joining-date-input" type="date" value={joiningDate} onChange={e => setJoiningDate(e.target.value)} />
            </div>
          )}
          {status === 'dropped' && (
            <div>
              <Label>Drop Reason</Label>
              <Input data-testid="drop-reason-input" value={dropReason} onChange={e => setDropReason(e.target.value)} placeholder="Reason for dropping..." />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} data-testid="confirm-status-update-btn">Update</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ========= MONTHLY SUMMARY TAB =========
const MonthlySummaryTab = ({ isAdmin }) => {
  const now = new Date();
  const defaultEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  const defaultStart = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`;

  const [startMonth, setStartMonth] = useState(defaultStart);
  const [endMonth, setEndMonth] = useState(defaultEnd);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchSummary = useCallback(async () => {
    if (!startMonth || !endMonth) return;
    setLoading(true);
    try {
      const res = await api.get('/recruiter-performance/monthly-summary', { params: { start_month: startMonth, end_month: endMonth } });
      setData(res.data);
    } catch (err) { console.error(err); toast.error('Failed to load monthly summary'); }
    finally { setLoading(false); }
  }, [startMonth, endMonth]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const toggleExpand = (rid) => setExpanded(prev => ({ ...prev, [rid]: !prev[rid] }));

  const handleExport = async () => {
    try {
      toast.info('Generating Excel report...');
      const res = await api.get('/recruiter-performance/monthly-summary/export', {
        params: { start_month: startMonth, end_month: endMonth },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `recruiter_monthly_summary_${startMonth}_to_${endMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (err) { console.error(err); toast.error('Failed to export'); }
  };

  const monthLabel = (m) => {
    const [y, mo] = m.split('-');
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${names[parseInt(mo) - 1]} ${y.slice(2)}`;
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full" /></div>;
  if (!data) return null;

  const { recruiters: recs, all_months, grand_months, grand_totals } = data;

  return (
    <div className="space-y-4">
      {/* Date Range Controls */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-slate-500">From</Label>
          <Input type="month" value={startMonth} onChange={e => setStartMonth(e.target.value)} className="w-[160px]" data-testid="summary-start-month" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">To</Label>
          <Input type="month" value={endMonth} onChange={e => setEndMonth(e.target.value)} className="w-[160px]" data-testid="summary-end-month" />
        </div>
        <Button variant="outline" size="sm" onClick={fetchSummary} data-testid="summary-refresh-btn">
          <RefreshCw size={16} className="mr-1" /> Refresh
        </Button>
        {recs.length > 0 && (
          <Button size="sm" onClick={handleExport} data-testid="summary-export-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Download size={16} className="mr-1" /> Export Excel
          </Button>
        )}
        <div className="ml-auto flex items-center gap-4 text-xs text-slate-500">
          {isAdmin ? (
            <>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-500 inline-block" /> Actual Rev.</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-blue-300 inline-block" /> Potential Rev.</span>
            </>
          ) : (
            <>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-cyan-500 inline-block" /> Joined CTC</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-slate-400 inline-block" /> J=Joinings / S=Selections</span>
            </>
          )}
        </div>
      </div>

      {recs.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400">No placement data found for the selected period.</CardContent></Card>
      ) : (
        <Card data-testid="monthly-summary-table-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse" data-testid="monthly-summary-table">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="py-3 px-4 text-left font-semibold text-slate-600 sticky left-0 bg-slate-50 z-10 min-w-[200px]">Recruiter</th>
                    {all_months.map(m => (
                      <th key={m} className="py-3 px-3 text-center font-medium text-slate-500 min-w-[120px]">{monthLabel(m)}</th>
                    ))}
                    <th className="py-3 px-4 text-right font-semibold text-slate-600 min-w-[120px] bg-slate-100">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {recs.map((r) => {
                    const isOpen = expanded[r.recruiter_id];
                    return (
                      <React.Fragment key={r.recruiter_id}>
                        {/* Main Row */}
                        <tr
                          className="border-b hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => toggleExpand(r.recruiter_id)}
                          data-testid={`summary-row-${r.recruiter_id}`}
                        >
                          <td className="py-3 px-4 sticky left-0 bg-white z-10">
                            <div className="flex items-center gap-2">
                              {isOpen ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />}
                              <div>
                                <p className="font-medium text-slate-800">{r.recruiter_name}</p>
                                <p className="text-xs text-slate-400">{r.recruiter_code}</p>
                              </div>
                            </div>
                          </td>
                          {all_months.map(m => {
                            const md = r.months[m] || {};
                            const hasData = isAdmin
                              ? ((md.actual_revenue || 0) > 0 || (md.potential_revenue || 0) > 0)
                              : ((md.total_ctc || 0) > 0 || (md.joinings || 0) > 0);
                            return (
                              <td key={m} className="py-3 px-3 text-center">
                                {hasData ? (
                                  <div>
                                    {isAdmin ? (
                                      <>
                                        <p className="font-semibold text-emerald-600 text-sm">{formatRevenue(md.actual_revenue || 0)}</p>
                                        <p className="text-xs text-blue-400">{formatRevenue(md.potential_revenue || 0)}</p>
                                      </>
                                    ) : (
                                      <>
                                        <p className="font-semibold text-cyan-700 text-sm">{formatCTC(md.joined_ctc || 0)}</p>
                                        <p className="text-xs text-slate-400">{md.joinings || 0}J / {md.selections || 0}S</p>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-slate-300">-</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="py-3 px-4 text-right bg-slate-50">
                            {isAdmin ? (
                              <>
                                <p className="font-bold text-emerald-600">{formatRevenue(r.total_actual)}</p>
                                <p className="text-xs text-blue-400">{formatRevenue(r.total_potential)}</p>
                              </>
                            ) : (
                              <>
                                <p className="font-bold text-cyan-700">{formatCTC(r.joined_ctc || 0)}</p>
                                <p className="text-xs text-slate-400">{r.total_joinings}J / {r.total_selections}S</p>
                              </>
                            )}
                          </td>
                        </tr>

                        {/* Expanded Detail Rows */}
                        {isOpen && (
                          <>
                            {/* Joinings row */}
                            <tr className="bg-emerald-50/40 border-b" data-testid={`expanded-joinings-${r.recruiter_id}`}>
                              <td className="py-2 px-4 pl-12 text-xs text-slate-500 sticky left-0 bg-emerald-50/40 z-10">Joinings</td>
                              {all_months.map(m => {
                                const md = r.months[m] || {};
                                return <td key={m} className="py-2 px-3 text-center text-xs font-medium text-emerald-700">{md.joinings || 0}</td>;
                              })}
                              <td className="py-2 px-4 text-right text-xs font-bold text-emerald-700 bg-emerald-50/60">{r.total_joinings}</td>
                            </tr>
                            {/* Selections row */}
                            <tr className="bg-blue-50/40 border-b" data-testid={`expanded-selections-${r.recruiter_id}`}>
                              <td className="py-2 px-4 pl-12 text-xs text-slate-500 sticky left-0 bg-blue-50/40 z-10">Selections</td>
                              {all_months.map(m => {
                                const md = r.months[m] || {};
                                return <td key={m} className="py-2 px-3 text-center text-xs font-medium text-blue-700">{md.selections || 0}</td>;
                              })}
                              <td className="py-2 px-4 text-right text-xs font-bold text-blue-700 bg-blue-50/60">{r.total_selections}</td>
                            </tr>
                            {/* Dropped row */}
                            <tr className="bg-red-50/30 border-b" data-testid={`expanded-dropped-${r.recruiter_id}`}>
                              <td className="py-2 px-4 pl-12 text-xs text-slate-500 sticky left-0 bg-red-50/30 z-10">Dropped</td>
                              {all_months.map(m => {
                                const md = r.months[m] || {};
                                return <td key={m} className="py-2 px-3 text-center text-xs font-medium text-red-600">{md.dropped || 0}</td>;
                              })}
                              <td className="py-2 px-4 text-right text-xs font-bold text-red-600 bg-red-50/40">{r.total_dropped}</td>
                            </tr>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* Grand Total Row */}
                  <tr className="bg-slate-100 border-t-2 border-slate-300 font-semibold" data-testid="summary-grand-total-row">
                    <td className="py-3 px-4 sticky left-0 bg-slate-100 z-10 text-slate-700">Grand Total</td>
                    {all_months.map(m => {
                      const gm = grand_months[m] || {};
                      return (
                        <td key={m} className="py-3 px-3 text-center">
                          {isAdmin ? (
                            <>
                              <p className="text-emerald-700 font-bold text-sm">{formatRevenue(gm.actual_revenue || 0)}</p>
                              <p className="text-xs text-blue-500">{formatRevenue(gm.potential_revenue || 0)}</p>
                            </>
                          ) : (
                            <>
                              <p className="text-cyan-700 font-bold text-sm">{formatCTC(gm.joined_ctc || 0)}</p>
                              <p className="text-xs text-slate-500">{gm.joinings || 0}J / {gm.selections || 0}S</p>
                            </>
                          )}
                        </td>
                      );
                    })}
                    <td className="py-3 px-4 text-right bg-slate-200">
                      {isAdmin ? (
                        <>
                          <p className="text-emerald-700 font-bold">{formatRevenue(grand_totals.actual_revenue || 0)}</p>
                          <p className="text-xs text-blue-500">{formatRevenue(grand_totals.potential_revenue || 0)}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-cyan-700 font-bold">{formatCTC(grand_totals.joined_ctc || 0)}</p>
                          <p className="text-xs text-slate-500">{grand_totals.joinings || 0} Joinings</p>
                        </>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {recs.length > 0 && (
        <div className={`grid grid-cols-2 ${isAdmin ? 'md:grid-cols-4' : 'md:grid-cols-4'} gap-4`} data-testid="summary-kpi-cards">
          {isAdmin && (
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-slate-500 uppercase">Total Actual Revenue</p>
                <p className="text-xl font-bold text-emerald-600 mt-1">{formatRevenue(grand_totals.actual_revenue || 0)}</p>
              </CardContent>
            </Card>
          )}
          {isAdmin && (
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-slate-500 uppercase">Total Potential Revenue</p>
                <p className="text-xl font-bold text-blue-600 mt-1">{formatRevenue(grand_totals.potential_revenue || 0)}</p>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-slate-500 uppercase">Total CTC (Joined)</p>
              <p className="text-xl font-bold text-cyan-700 mt-1">{formatCTC(grand_totals.joined_ctc || 0)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-slate-500 uppercase">Total Joinings</p>
              <p className="text-xl font-bold text-indigo-600 mt-1">{grand_totals.joinings || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-slate-500 uppercase">Total Selections</p>
              <p className="text-xl font-bold text-amber-600 mt-1">{grand_totals.selections || 0}</p>
            </CardContent>
          </Card>
          {isAdmin && (
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-slate-500 uppercase">Realization Rate</p>
                <p className="text-xl font-bold text-purple-600 mt-1">
                  {grand_totals.potential_revenue ? Math.round((grand_totals.actual_revenue / grand_totals.potential_revenue) * 100) : 0}%
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

// ========= MAIN COMPONENT =========
const RecruiterPerformance = ({ user, setUser }) => {
  const isAdmin = user?.role === 'admin' || user?.role === 'hr_manager';
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Data
  const [dashboard, setDashboard] = useState({ kpis: {}, leaderboard: [], client_revenue: [], trends: [] });
  const [alerts, setAlerts] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [clients, setClients] = useState([]);
  const [recruiters, setRecruiters] = useState([]);

  // Filters
  const [filterRecruiter, setFilterRecruiter] = useState('all');
  const [recruiterDropdownOpen, setRecruiterDropdownOpen] = useState(false);
  const [recruiterSearchTerm, setRecruiterSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [candidateSearch, setCandidateSearch] = useState('');

  // Dialogs
  const [clientDialog, setClientDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [placementDialog, setPlacementDialog] = useState(false);
  const [editingPlacement, setEditingPlacement] = useState(null);
  const [statusDialog, setStatusDialog] = useState(false);
  const [statusPlacement, setStatusPlacement] = useState(null);
  const [bulkUploadType, setBulkUploadType] = useState(null); // 'clients' or 'placements'
  const [uploading, setUploading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientSort, setClientSort] = useState({ field: 'client_name', dir: 'asc' });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const dashParams = {};
      if (isAdmin && filterRecruiter !== 'all') dashParams.recruiter_id = filterRecruiter;

      const placementParams = {};
      if (isAdmin && filterRecruiter !== 'all') placementParams.recruiter_id = filterRecruiter;
      if (filterStatus !== 'all') placementParams.status = filterStatus;

      const [dashRes, alertsRes, placementsRes, clientsRes, recruitersRes] = await Promise.all([
        api.get('/recruiter-performance/dashboard', { params: dashParams }),
        api.get('/recruiter-performance/alerts'),
        api.get('/recruiter-performance/placements', { params: placementParams }),
        api.get('/recruiter-performance/client-billing'),
        isAdmin ? api.get('/recruiter-performance/recruiters') : Promise.resolve({ data: [] }),
      ]);

      setDashboard(dashRes.data);
      setAlerts(alertsRes.data.alerts || []);
      setPlacements(placementsRes.data);
      setClients(clientsRes.data);
      setRecruiters(recruitersRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load recruiter data');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, filterRecruiter, filterStatus]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredPlacements = candidateSearch.trim()
    ? placements.filter(p => (p.candidate_name || '').toLowerCase().includes(candidateSearch.trim().toLowerCase()))
    : placements;

  // Client CRUD
  const handleSaveClient = async (data) => {
    try {
      if (editingClient) await api.put(`/recruiter-performance/client-billing/${editingClient.id}`, data);
      else await api.post('/recruiter-performance/client-billing', data);
      toast.success(editingClient ? 'Client updated' : 'Client created');
      setClientDialog(false);
      setEditingClient(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm('Delete this client billing config?')) return;
    try {
      await api.delete(`/recruiter-performance/client-billing/${id}`);
      toast.success('Client deleted');
      fetchAll();
    } catch (err) { toast.error('Failed to delete'); }
  };

  // Placement CRUD
  const handleSavePlacement = async (data) => {
    try {
      if (editingPlacement) await api.put(`/recruiter-performance/placements/${editingPlacement.id}`, data);
      else await api.post('/recruiter-performance/placements', data);
      toast.success(editingPlacement ? 'Placement updated' : 'Placement created');
      setPlacementDialog(false);
      setEditingPlacement(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeletePlacement = async (id) => {
    if (!window.confirm('Delete this placement?')) return;
    try {
      await api.delete(`/recruiter-performance/placements/${id}`);
      toast.success('Deleted');
      fetchAll();
    } catch (err) { toast.error('Failed to delete'); }
  };

  // Status update
  const handleStatusUpdate = async (data) => {
    try {
      await api.put(`/recruiter-performance/placements/${statusPlacement.id}`, data);
      toast.success('Status updated');
      setStatusDialog(false);
      setStatusPlacement(null);
      fetchAll();
    } catch (err) { toast.error('Failed to update status'); }
  };

  // Bulk upload
  const handleDownloadTemplate = async (type) => {
    try {
      const url = type === 'clients' ? '/recruiter-performance/client-billing/template/download' : '/recruiter-performance/placements/template/download';
      const res = await api.get(url, { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(res.data);
      link.download = type === 'clients' ? 'client_billing_template.xlsx' : 'placements_template.xlsx';
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) { toast.error('Failed to download template'); }
  };

  const handleBulkUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const url = bulkUploadType === 'clients' ? '/recruiter-performance/client-billing/bulk-upload' : '/recruiter-performance/placements/bulk-upload';
      const res = await api.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { created, updated, errors } = res.data;
      const label = bulkUploadType === 'clients' ? 'clients' : 'placements';
      if (created > 0 || updated > 0) toast.success(`${created} created, ${updated || 0} updated (${label})`);
      if (errors?.length > 0) {
        errors.slice(0, 5).forEach(e => toast.error(`Row ${e.row}: ${e.error}`));
        if (errors.length > 5) toast.error(`...and ${errors.length - 5} more errors`);
      }
      setBulkUploadType(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDedup = async () => {
    try {
      const res = await api.post('/recruiter-performance/placements/dedup');
      const { removed, kept } = res.data;
      if (removed > 0) {
        toast.success(`Removed ${removed} duplicates. ${kept} unique records kept.`);
        fetchAll();
      } else {
        toast.info('No duplicates found');
      }
    } catch (err) {
      toast.error('Failed to remove duplicates');
    }
  };

  const { kpis, leaderboard, client_revenue, trends } = dashboard;

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="recruiter-performance-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">Recruiter Performance</h1>
            <p className="text-sm text-slate-500">Track placements, revenue, and recruiter efficiency</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Popover open={recruiterDropdownOpen} onOpenChange={setRecruiterDropdownOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[200px] justify-between font-normal" data-testid="filter-recruiter">
                    {filterRecruiter === 'all' ? 'All Recruiters' : (recruiters.find(r => r.id === filterRecruiter)?.name || 'All Recruiters')}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0" align="end">
                  <Command>
                    <CommandInput placeholder="Search recruiter..." value={recruiterSearchTerm} onValueChange={setRecruiterSearchTerm} data-testid="recruiter-search-input" />
                    <CommandList>
                      <CommandEmpty>No recruiter found</CommandEmpty>
                      <CommandGroup>
                        <CommandItem value="all" onSelect={() => { setFilterRecruiter('all'); setRecruiterDropdownOpen(false); setRecruiterSearchTerm(''); }}>
                          <Check className={`mr-2 h-4 w-4 ${filterRecruiter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                          All Recruiters
                        </CommandItem>
                        {recruiters.map(r => (
                          <CommandItem key={r.id} value={r.name} onSelect={() => { setFilterRecruiter(r.id); setRecruiterDropdownOpen(false); setRecruiterSearchTerm(''); }}>
                            <Check className={`mr-2 h-4 w-4 ${filterRecruiter === r.id ? 'opacity-100' : 'opacity-0'}`} />
                            {r.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
            <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-btn">
              <RefreshCw size={16} />
            </Button>
          </div>
        </div>

        {/* Alerts Banner */}
        {alerts.length > 0 && (
          <div className="space-y-2" data-testid="alerts-section">
            {alerts.filter(a => isAdmin || a.type !== 'top_revenue').map((a, i) => (
              <div
                key={i}
                data-testid={`alert-${a.type}`}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  a.type === 'top_revenue'
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : a.severity === 'critical'
                    ? 'bg-red-50 border-red-200 text-red-800'
                    : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}
              >
                {a.type === 'top_revenue' ? <Award size={18} /> : <AlertTriangle size={18} />}
                <span className="text-sm font-medium">{a.message}</span>
                {isAdmin && a.actual_revenue > 0 && (
                  <Badge className="ml-auto bg-emerald-100 text-emerald-700">{formatRevenue(a.actual_revenue)}</Badge>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="main-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="monthly-summary" data-testid="tab-monthly-summary">Monthly Summary</TabsTrigger>
            <TabsTrigger value="placements" data-testid="tab-placements">Placements</TabsTrigger>
            {isAdmin && <TabsTrigger value="clients" data-testid="tab-clients">Clients</TabsTrigger>}
          </TabsList>

          {/* ===== OVERVIEW TAB ===== */}
          <TabsContent value="overview" className="space-y-6">
            {/* KPI Cards */}
            <div className={`grid grid-cols-2 md:grid-cols-3 ${isAdmin ? 'lg:grid-cols-5' : 'lg:grid-cols-5'} gap-4`} data-testid="kpi-grid">
              <KPICard title="Total Joinings" value={kpis.total_joinings || 0} icon={UserCheck} color="emerald" subtitle="Confirmed hires" />
              <KPICard title="Total CTC" value={formatCTC(kpis.total_ctc || 0)} icon={DollarSign} color="cyan" subtitle="All candidates" />
              <KPICard title="Avg CTC (Joined)" value={formatCTC(kpis.avg_joined_ctc || 0)} icon={Target} color="blue" subtitle={`${kpis.total_joinings || 0} joinings`} />
              {isAdmin && <KPICard title="Actual Revenue" value={`${formatRevenue(kpis.actual_revenue || 0)}`} icon={DollarSign} color="emerald" subtitle="From joinings" />}
              {isAdmin && <KPICard title="Potential Revenue" value={`${formatRevenue(kpis.potential_revenue || 0)}`} icon={Target} color="blue" subtitle="All selections" />}
              <KPICard title="Offer-to-Join" value={`${kpis.offer_to_join_ratio || 0}%`} icon={TrendingUp} color="indigo" subtitle={`${kpis.total_offered || 0} offers`} />
              <KPICard title="Dropout Rate" value={`${kpis.dropout_rate || 0}%`} icon={kpis.dropout_rate > 30 ? TrendingDown : UserX} color={kpis.dropout_rate > 30 ? 'red' : 'amber'} subtitle={`${kpis.total_dropped || 0} dropped`} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Monthly Trends */}
              <Card data-testid="monthly-trends-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 size={18} className="text-indigo-500" /> Monthly Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {trends.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">No data yet</p>
                  ) : (
                    <div className="space-y-3">
                      {trends.slice(-6).map(t => {
                        const maxVal = isAdmin
                          ? Math.max(...trends.map(x => x.potential_revenue || 1))
                          : Math.max(...trends.map(x => x.total_ctc || 1));
                        const pct = isAdmin
                          ? Math.round(((t.potential_revenue || 0) / maxVal) * 100)
                          : Math.round(((t.total_ctc || 0) / maxVal) * 100);
                        const actualPct = isAdmin
                          ? Math.round(((t.actual_revenue || 0) / maxVal) * 100)
                          : Math.round(((t.joined_ctc || 0) / maxVal) * 100);
                        return (
                          <div key={t.month} className="space-y-1" data-testid={`trend-${t.month}`}>
                            <div className="flex justify-between text-xs text-slate-600">
                              <span className="font-medium">{t.month}</span>
                              <span>{t.joinings} joined / {t.selections} selected</span>
                            </div>
                            <div className="relative h-5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="absolute inset-y-0 left-0 bg-blue-200 rounded-full" style={{ width: `${pct}%` }} />
                              <div className="absolute inset-y-0 left-0 bg-emerald-500 rounded-full" style={{ width: `${actualPct}%` }} />
                            </div>
                            {isAdmin ? (
                              <div className="flex justify-between text-xs text-slate-400">
                                <span>Actual: {formatRevenue(t.actual_revenue)}</span>
                                <span>Potential: {formatRevenue(t.potential_revenue)}</span>
                              </div>
                            ) : (
                              <div className="flex justify-between text-xs text-slate-400">
                                <span>Joined CTC: {formatCTC(t.joined_ctc)}</span>
                                <span>Total CTC: {formatCTC(t.total_ctc)}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="flex items-center gap-4 pt-2 text-xs text-slate-400 border-t">
                        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-500" /> {isAdmin ? 'Actual' : 'Joined CTC'}</div>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-blue-200" /> {isAdmin ? 'Potential' : 'Total CTC'}</div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recruiter Leaderboard */}
              <Card data-testid="leaderboard-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Award size={18} className="text-amber-500" /> Recruiter Leaderboard
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {leaderboard.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">No data yet</p>
                  ) : (
                    <div className="space-y-2">
                      {leaderboard.map((r, i) => (
                        <div key={r.recruiter_id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition" data-testid={`leaderboard-row-${i}`}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                            i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-slate-300 text-white' : i === 2 ? 'bg-amber-600 text-white' : 'bg-slate-200 text-slate-600'
                          }`}>
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{r.recruiter_name}</p>
                            <p className="text-xs text-slate-400">{r.recruiter_code} &middot; {r.joinings}J / {r.selections}S</p>
                          </div>
                          <div className="text-right">
                            {isAdmin ? (
                              <>
                                <p className="text-sm font-semibold text-emerald-600">{formatRevenue(r.actual_revenue)}</p>
                                <p className="text-xs text-slate-400">CTC: {formatCTC(r.total_ctc)}</p>
                              </>
                            ) : (
                              <>
                                <p className="text-sm font-semibold text-cyan-700">{formatCTC(r.total_ctc)}</p>
                                <p className="text-xs text-slate-400">{r.joinings}J / {r.selections}S</p>
                              </>
                            )}
                          </div>
                          {r.dropout_rate >= 50 && (
                            <AlertTriangle size={16} className="text-red-400 flex-shrink-0" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Client Revenue Table - Admin only */}
            {isAdmin && client_revenue.length > 0 && (
              <Card data-testid="client-revenue-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Briefcase size={18} className="text-blue-500" /> Revenue by Client
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-slate-500">
                          <th className="py-2 pr-4">Client</th>
                          <th className="py-2 pr-4 text-right">Selections</th>
                          <th className="py-2 pr-4 text-right">Joinings</th>
                          <th className="py-2 pr-4 text-right">Conversion</th>
                          <th className="py-2 pr-4 text-right">Potential Rev.</th>
                          <th className="py-2 text-right">Actual Rev.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {client_revenue.map(c => (
                          <tr key={c.client_name} className="border-b last:border-0 hover:bg-slate-50" data-testid={`client-rev-${c.client_name}`}>
                            <td className="py-2.5 pr-4 font-medium text-slate-800">{c.client_name}</td>
                            <td className="py-2.5 pr-4 text-right">{c.selections}</td>
                            <td className="py-2.5 pr-4 text-right text-emerald-600 font-medium">{c.joinings}</td>
                            <td className="py-2.5 pr-4 text-right">{c.selections ? Math.round((c.joinings / c.selections) * 100) : 0}%</td>
                            <td className="py-2.5 pr-4 text-right text-blue-600">{formatRevenue(c.potential_revenue)}</td>
                            <td className="py-2.5 text-right text-emerald-600 font-semibold">{formatRevenue(c.actual_revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== MONTHLY SUMMARY TAB ===== */}
          <TabsContent value="monthly-summary">
            <MonthlySummaryTab isAdmin={isAdmin} />
          </TabsContent>

          {/* ===== PLACEMENTS TAB ===== */}
          <TabsContent value="placements" className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search candidate..."
                    value={candidateSearch}
                    onChange={e => setCandidateSearch(e.target.value)}
                    className="pl-8 w-[200px] h-9"
                    data-testid="candidate-search-input"
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[160px]" data-testid="filter-status">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="selected">Selected</SelectItem>
                    <SelectItem value="offered">Offered</SelectItem>
                    <SelectItem value="joined">Joined</SelectItem>
                    <SelectItem value="dropped">Dropped</SelectItem>
                  </SelectContent>
                </Select>
                <Badge variant="outline">{filteredPlacements.length} / {placements.length}</Badge>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate('placements')} data-testid="download-placement-template-btn">
                    <Download size={16} className="mr-1" /> Template
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setBulkUploadType('placements')} data-testid="bulk-upload-placements-btn">
                    <Upload size={16} className="mr-1" /> Bulk Upload
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleDedup} data-testid="dedup-placements-btn">
                    <Trash2 size={16} className="mr-1" /> Remove Duplicates
                  </Button>
                  <Button size="sm" onClick={() => { setEditingPlacement(null); setPlacementDialog(true); }} data-testid="add-placement-btn">
                    <Plus size={16} className="mr-1" /> Add Placement
                  </Button>
                </div>
              )}
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="placements-table">
                    <thead>
                      <tr className="border-b bg-slate-50 text-left text-slate-500">
                        <th className="py-3 px-4">Candidate</th>
                        <th className="py-3 px-4">Position</th>
                        <th className="py-3 px-4">Recruiter</th>
                        <th className="py-3 px-4">Client</th>
                        <th className="py-3 px-4 text-center">Selection Date</th>
                        <th className="py-3 px-4 text-center">Joining Date</th>
                        <th className="py-3 px-4 text-right">CTC (Annual)</th>
                        {isAdmin && <th className="py-3 px-4 text-right">Pot. Rev</th>}
                        {isAdmin && <th className="py-3 px-4 text-right">Act. Rev</th>}
                        <th className="py-3 px-4 text-center">Status</th>
                        <th className="py-3 px-4 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPlacements.length === 0 ? (
                        <tr><td colSpan="11" className="py-12 text-center text-slate-400">{candidateSearch ? 'No matching candidates' : 'No placements found.'} {!candidateSearch && isAdmin && 'Click "Add Placement" to get started.'}</td></tr>
                      ) : filteredPlacements.map(p => (
                        <tr key={p.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`placement-row-${p.id}`}>
                          <td className="py-3 px-4">
                            <p className="font-medium text-slate-800">{p.candidate_name}</p>
                            {p.candidate_email && <p className="text-xs text-slate-400">{p.candidate_email}</p>}
                          </td>
                          <td className="py-3 px-4 text-slate-600">{p.position}</td>
                          <td className="py-3 px-4">
                            <p className="text-slate-700">{p.recruiter_name}</p>
                            <p className="text-xs text-slate-400">{p.recruiter_code}</p>
                          </td>
                          <td className="py-3 px-4 text-slate-600">{p.client_name}</td>
                          <td className="py-3 px-4 text-center text-slate-600 text-xs">{p.selection_date || '-'}</td>
                          <td className="py-3 px-4 text-center text-slate-600 text-xs">{p.joining_date || '-'}</td>
                          <td className="py-3 px-4 text-right text-slate-700"><span>{formatCTC(p.candidate_ctc)}</span><span className="text-xs text-slate-400"> /yr</span></td>
                          {isAdmin && <td className="py-3 px-4 text-right text-blue-600">{formatRevenue(p.potential_revenue)}</td>}
                          {isAdmin && <td className="py-3 px-4 text-right text-emerald-600 font-medium">{formatRevenue(p.actual_revenue)}</td>}
                          <td className="py-3 px-4 text-center"><StatusBadge status={p.status} /></td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => { setStatusPlacement(p); setStatusDialog(true); }} data-testid={`update-status-${p.id}`}>
                                <ChevronRight size={16} />
                              </Button>
                              {isAdmin && (
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeletePlacement(p.id)} data-testid={`delete-placement-${p.id}`}>
                                  <Trash2 size={16} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== CLIENTS TAB (Admin Only) ===== */}
          {isAdmin && (
            <TabsContent value="clients" className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                    <Input
                      placeholder="Search clients..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="pl-8 w-56 h-9"
                      data-testid="client-search-input"
                    />
                  </div>
                  <p className="text-sm text-slate-500">
                    {(() => {
                      const filtered = clients.filter(c => !clientSearch || c.client_name.toLowerCase().includes(clientSearch.toLowerCase()) || (c.industry || '').toLowerCase().includes(clientSearch.toLowerCase()));
                      return `${filtered.length} of ${clients.length} clients`;
                    })()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate('clients')} data-testid="download-client-template-btn">
                    <Download size={16} className="mr-1" /> Template
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setBulkUploadType('clients')} data-testid="bulk-upload-clients-btn">
                    <Upload size={16} className="mr-1" /> Bulk Upload
                  </Button>
                  <Button size="sm" onClick={() => { setEditingClient(null); setClientDialog(true); }} data-testid="add-client-btn">
                    <Plus size={16} className="mr-1" /> Add Client
                  </Button>
                </div>
              </div>

              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="clients-table">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          {[
                            { key: 'client_name', label: 'Client Name' },
                            { key: 'industry', label: 'Industry' },
                            { key: 'billing_type', label: 'Billing Type' },
                            { key: 'rate', label: 'Rate / Fee' },
                            { key: 'notes', label: 'Notes' },
                          ].map(col => (
                            <th
                              key={col.key}
                              className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 select-none"
                              onClick={() => setClientSort(prev => ({ field: col.key, dir: prev.field === col.key && prev.dir === 'asc' ? 'desc' : 'asc' }))}
                              data-testid={`sort-${col.key}`}
                            >
                              <span className="flex items-center gap-1">
                                {col.label}
                                <ArrowUpDown size={12} className={clientSort.field === col.key ? 'text-indigo-600' : 'text-slate-300'} />
                              </span>
                            </th>
                          ))}
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let filtered = clients.filter(c => !clientSearch || c.client_name.toLowerCase().includes(clientSearch.toLowerCase()) || (c.industry || '').toLowerCase().includes(clientSearch.toLowerCase()));
                          filtered.sort((a, b) => {
                            const dir = clientSort.dir === 'asc' ? 1 : -1;
                            if (clientSort.field === 'client_name') return dir * a.client_name.localeCompare(b.client_name);
                            if (clientSort.field === 'industry') return dir * (a.industry || '').localeCompare(b.industry || '');
                            if (clientSort.field === 'billing_type') return dir * (a.billing_type || '').localeCompare(b.billing_type || '');
                            if (clientSort.field === 'rate') {
                              const aVal = a.billing_type === 'flat' ? (a.flat_fee || 0) : (a.percentage || 0);
                              const bVal = b.billing_type === 'flat' ? (b.flat_fee || 0) : (b.percentage || 0);
                              return dir * (aVal - bVal);
                            }
                            if (clientSort.field === 'notes') return dir * (a.notes || '').localeCompare(b.notes || '');
                            return 0;
                          });
                          if (filtered.length === 0) {
                            return (
                              <tr>
                                <td colSpan={6} className="text-center py-12 text-slate-400">
                                  {clientSearch ? 'No clients matching your search' : 'No clients configured. Add your first client billing config.'}
                                </td>
                              </tr>
                            );
                          }
                          return filtered.map(c => (
                            <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`client-row-${c.id}`}>
                              <td className="py-3 px-4">
                                <p className="font-medium text-slate-800">{c.client_name}</p>
                              </td>
                              <td className="py-3 px-4 text-sm text-slate-600">{c.industry || '-'}</td>
                              <td className="py-3 px-4">
                                <Badge className={c.billing_type === 'flat' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                                  {c.billing_type === 'flat' ? 'Flat Fee' : 'Percentage'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                <span className="font-semibold text-slate-700">
                                  {c.billing_type === 'flat' ? formatRevenue(c.flat_fee) : `${c.percentage}% of CTC`}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-slate-500 max-w-xs truncate">{c.notes || '-'}</td>
                              <td className="py-3 px-4 text-center">
                                <div className="flex justify-center gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => { setEditingClient(c); setClientDialog(true); }} data-testid={`edit-client-${c.id}`}>
                                    <Edit2 size={14} />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteClient(c.id)} data-testid={`delete-client-${c.id}`}>
                                    <Trash2 size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Dialogs */}
      <ClientBillingDialog open={clientDialog} onClose={() => { setClientDialog(false); setEditingClient(null); }} onSave={handleSaveClient} editing={editingClient} />
      <PlacementDialog open={placementDialog} onClose={() => { setPlacementDialog(false); setEditingPlacement(null); }} onSave={handleSavePlacement} editing={editingPlacement} recruiters={recruiters} clients={clients} />
      <StatusUpdateDialog open={statusDialog} onClose={() => { setStatusDialog(false); setStatusPlacement(null); }} onSave={handleStatusUpdate} placement={statusPlacement} />

      {/* Bulk Upload Dialog */}
      <Dialog open={!!bulkUploadType} onOpenChange={() => setBulkUploadType(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="bulk-upload-dialog-title">
              Bulk Upload {bulkUploadType === 'clients' ? 'Clients' : 'Placements'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <p className="font-medium mb-1">Instructions:</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Download the template using the "Template" button</li>
                <li>Fill in the data (see sample rows & reference sheet)</li>
                <li>Upload the filled Excel file below</li>
              </ol>
            </div>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
              <Upload className="mx-auto mb-2 text-slate-400" size={32} />
              <p className="text-sm text-slate-600 mb-3">Select Excel file (.xlsx)</p>
              <input
                type="file"
                accept=".xlsx,.xls"
                data-testid="bulk-upload-file-input"
                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                onChange={(e) => handleBulkUpload(e.target.files[0])}
                disabled={uploading}
              />
            </div>
            {uploading && (
              <div className="flex items-center gap-2 text-sm text-indigo-600">
                <div className="animate-spin h-4 w-4 border-2 border-indigo-500 border-t-transparent rounded-full"></div>
                Uploading...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default RecruiterPerformance;
